import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Home, 
  Shield, 
  Activity, 
  Search, 
  Filter, 
  Eye, 
  Download, 
  RefreshCw,
  Server,
  Database,
  Cpu,
  HardDrive,
  Network,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Mock system activity data
const generateSystemActivityData = () => {
  const activities = [
    'System startup',
    'Database connection established',
    'API endpoint accessed',
    'Configuration file modified',
    'Log file rotated',
    'Backup process initiated',
    'Cache cleared',
    'Service restarted',
    'SSL certificate renewed',
    'Database migration executed',
    'Scheduled task executed',
    'System health check',
    'Memory cleanup',
    'Disk space check',
    'Network connectivity test',
    'Security scan completed',
    'Update installation',
    'Service deployment',
    'Database optimization',
    'Error log analysis'
  ];

  const services = ['Authentication', 'API Gateway', 'Database', 'File Storage', 'Cache', 'Analytics', 'Notifications', 'Backup'];
  const severities = ['Info', 'Warning', 'Error', 'Critical'];
  const severityColors = {
    'Info': 'bg-blue-100 text-blue-800 border-blue-200',
    'Warning': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Error': 'bg-red-100 text-red-800 border-red-200',
    'Critical': 'bg-purple-100 text-purple-800 border-purple-200',
  };

  return Array.from({ length: 500 }, (_, i) => {
    const activity = activities[Math.floor(Math.random() * activities.length)];
    const service = services[Math.floor(Math.random() * services.length)];
    const severity = severities[Math.floor(Math.random() * severities.length)];
    const timestamp = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000);
    
    return {
      id: `sys-${i + 1}`,
      timestamp,
      activity,
      service,
      severity,
      severityColor: severityColors[severity as keyof typeof severityColors],
      description: `${activity} in ${service} service`,
      details: generateActivityDetails(activity || '', service || ''),
      duration: Math.floor(Math.random() * 5000) + 100, // milliseconds
      resourceUsage: {
        cpu: Math.floor(Math.random() * 100),
        memory: Math.floor(Math.random() * 100),
        disk: Math.floor(Math.random() * 100),
        network: Math.floor(Math.random() * 100),
      },
      errorCode: severity === 'Error' || severity === 'Critical' ? `ERR-${Math.floor(Math.random() * 9000) + 1000}` : null,
      resolved: severity === 'Error' || severity === 'Critical' ? Math.random() > 0.3 : true,
    };
  }).sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

const generateActivityDetails = (activity: string, service: string) => {
  const details = {
    'System startup': `${service} service initialized successfully. All dependencies loaded. Ready to accept connections.`,
    'Database connection established': `Connection pool established with ${service} database. Pool size: 10 connections. Response time: 45ms.`,
    'API endpoint accessed': `${service} API endpoint /v1/data accessed. Response code: 200. Processing time: 125ms.`,
    'Configuration file modified': `${service} configuration updated. Previous version backed up. Service restart required.`,
    'Scheduled task executed': `${service} scheduled maintenance task completed successfully. Next execution: 24 hours.`,
    'Error log analysis': `${service} error logs analyzed. 3 warnings found. Automatic remediation applied.`,
  };
  
  return details[activity as keyof typeof details] || `${activity} completed in ${service} service with standard parameters.`;
};

export function SystemActivityLog() {
  const [systemData] = useState(generateSystemActivityData());
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
    to: new Date(),
  });
  const [filters, setFilters] = useState({
    service: 'all',
    severity: 'all',
    searchQuery: '',
    resolved: 'all',
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    return systemData.filter(activity => {
      // Date range filter
      if (dateRange.from && dateRange.to) {
        if (activity.timestamp < dateRange.from || activity.timestamp > dateRange.to) {
          return false;
        }
      }

      // Service filter
      if (filters.service !== 'all' && activity.service !== filters.service) {
        return false;
      }

      // Severity filter
      if (filters.severity !== 'all' && activity.severity !== filters.severity) {
        return false;
      }

      // Resolved filter
      if (filters.resolved !== 'all') {
        if (filters.resolved === 'resolved' && !activity.resolved) return false;
        if (filters.resolved === 'unresolved' && activity.resolved) return false;
      }

      // Search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return (
          activity.activity?.toLowerCase().includes(query) ||
          activity.service?.toLowerCase().includes(query) ||
          activity.description?.toLowerCase().includes(query) ||
          (activity.errorCode && activity.errorCode.toLowerCase().includes(query))
        );
      }

      return true;
    });
  }, [systemData, dateRange, filters]);

  const handleViewDetails = (activity: any) => {
    setSelectedActivity(activity);
    setShowDetails(true);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success('System activity log refreshed');
    }, 1000);
  };

  const handleExport = () => {
    toast.success('System activity log exported to CSV');
  };

  const getUniqueServices = () => {
    return Array.from(new Set(systemData.map(activity => activity.service))).sort();
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = filteredData.length;
    const errors = filteredData.filter(a => a.severity === 'Error' || a.severity === 'Critical').length;
    const warnings = filteredData.filter(a => a.severity === 'Warning').length;
    const unresolved = filteredData.filter(a => !a.resolved).length;
    const avgDuration = total > 0 ? (filteredData.reduce((sum, a) => sum + a.duration, 0) / total).toFixed(0) : '0';
    
    return { total, errors, warnings, unresolved, avgDuration };
  }, [filteredData]);

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        {/* Breadcrumb Navigation */}
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Shield className="w-3 h-3" />
                  Admin Audit
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage style={{ fontSize: '11.2px' }}>System Activity</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                System Activity Log
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Comprehensive tracking of all system operations and events
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Activity className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.total}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Total Activities
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <XCircle className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.errors}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Errors
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.warnings}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Warnings
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.unresolved}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Unresolved
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {summaryStats.avgDuration}ms
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>
                      Avg Duration
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Filters */}
        <div className="px-8 pb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                <Filter className="w-4 h-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                {/* Date Range */}
                <div className="md:col-span-2">
                  <DateRangeFilter
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                  />
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search activities..."
                    value={filters.searchQuery}
                    onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                    className="pl-9"
                    style={{ fontSize: '11.2px' }}
                  />
                </div>

                {/* Service Filter */}
                <Select value={filters.service} onValueChange={(value) => setFilters(prev => ({ ...prev, service: value }))}>
                  <SelectTrigger style={{ fontSize: '11.2px' }}>
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {getUniqueServices().map((service) => (
                      <SelectItem key={service} value={service || ''}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Severity Filter */}
                <Select value={filters.severity} onValueChange={(value) => setFilters(prev => ({ ...prev, severity: value }))}>
                  <SelectTrigger style={{ fontSize: '11.2px' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severities</SelectItem>
                    <SelectItem value="Info">Info</SelectItem>
                    <SelectItem value="Warning">Warning</SelectItem>
                    <SelectItem value="Error">Error</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>

                {/* Resolved Filter */}
                <Select value={filters.resolved} onValueChange={(value) => setFilters(prev => ({ ...prev, resolved: value }))}>
                  <SelectTrigger style={{ fontSize: '11.2px' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="unresolved">Unresolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Log Table */}
        <div className="flex-1 px-8 pb-8">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>System Activities ({filteredData.length} entries)</CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Detailed log of all system operations and events
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ fontSize: '11.2px' }}>Timestamp</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Service</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Activity</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Severity</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Duration</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Status</TableHead>
                      <TableHead style={{ fontSize: '11.2px' }}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.slice(0, 50).map((activity) => (
                      <TableRow key={activity.id} className={!activity.resolved ? 'bg-red-50' : ''}>
                        <TableCell style={{ fontSize: '11.2px' }}>
                          <div>
                            <p>{activity.timestamp.toLocaleDateString()}</p>
                            <p className="text-gray-500" style={{ fontSize: '9.6px' }}>
                              {activity.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Server className="w-4 h-4 text-gray-400" />
                            <span style={{ fontSize: '11.2px', fontWeight: 500 }}>
                              {activity.service}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p style={{ fontSize: '11.2px', fontWeight: 500 }}>
                              {activity.activity}
                            </p>
                            {activity.errorCode && (
                              <p className="text-red-600" style={{ fontSize: '9.6px' }}>
                                {activity.errorCode}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={activity.severityColor}>
                            {activity.severity}
                          </Badge>
                        </TableCell>
                        <TableCell style={{ fontSize: '11.2px' }}>
                          {activity.duration}ms
                        </TableCell>
                        <TableCell>
                          {activity.resolved ? (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                              Resolved
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                              Unresolved
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => handleViewDetails(activity)}>
                            <Eye className="w-3 h-3 mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Details Dialog */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>System Activity Details</DialogTitle>
              <DialogDescription>
                Comprehensive information about the selected system activity
              </DialogDescription>
            </DialogHeader>
            
            {selectedActivity && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Timestamp</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.timestamp.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Service</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.service}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Activity</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.activity}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Duration</p>
                    <p className="text-gray-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                      {selectedActivity.duration}ms
                    </p>
                  </div>
                </div>

                {/* Resource Usage */}
                <div>
                  <h4 className="text-gray-900 mb-3" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Resource Usage
                  </h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <Cpu className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                      <p className="text-blue-900" style={{ fontSize: '14px', fontWeight: 600 }}>
                        {selectedActivity.resourceUsage.cpu}%
                      </p>
                      <p className="text-blue-700" style={{ fontSize: '9.6px' }}>CPU</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center">
                      <Database className="w-6 h-6 text-green-500 mx-auto mb-1" />
                      <p className="text-green-900" style={{ fontSize: '14px', fontWeight: 600 }}>
                        {selectedActivity.resourceUsage.memory}%
                      </p>
                      <p className="text-green-700" style={{ fontSize: '9.6px' }}>Memory</p>
                    </div>
                    <div className="p-3 bg-yellow-50 rounded-lg text-center">
                      <HardDrive className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                      <p className="text-yellow-900" style={{ fontSize: '14px', fontWeight: 600 }}>
                        {selectedActivity.resourceUsage.disk}%
                      </p>
                      <p className="text-yellow-700" style={{ fontSize: '9.6px' }}>Disk</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg text-center">
                      <Network className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                      <p className="text-purple-900" style={{ fontSize: '14px', fontWeight: 600 }}>
                        {selectedActivity.resourceUsage.network}%
                      </p>
                      <p className="text-purple-700" style={{ fontSize: '9.6px' }}>Network</p>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div>
                  <h4 className="text-gray-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Activity Details
                  </h4>
                  <div className="p-4 bg-gray-50 rounded-lg border">
                    <p style={{ fontSize: '11.2px' }}>{selectedActivity.details}</p>
                  </div>
                </div>

                {selectedActivity.errorCode && (
                  <div>
                    <h4 className="text-gray-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                      Error Information
                    </h4>
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800" style={{ fontSize: '11.2px' }}>
                        <strong>Error Code:</strong> {selectedActivity.errorCode}
                      </p>
                      <p className="text-red-700 mt-2" style={{ fontSize: '11.2px' }}>
                        This error indicates a system-level issue that requires investigation. 
                        Check system logs and resource availability.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}